import { useState, useEffect, useMemo } from "react";
import { providerDomain, domainFromWebsite, logoUrlForDomain, logoUrlForName, initials, avatarColor } from "@/lib/brandLogos";

/**
 * Brand icon for an integration provider. Renders the real brand logo (by domain)
 * with a graceful fallback to the category Phosphor icon when no logo is available.
 */
export function ProviderLogo({ provider, fallbackIcon: FallbackIcon, size = 28, className = "" }) {
  const domain = providerDomain(provider);
  const url = logoUrlForDomain(domain);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);

  const box = `flex shrink-0 items-center justify-center border border-border bg-white ${className}`;
  const style = { width: size, height: size };

  if (url && !failed) {
    return (
      <span className={box} style={style} data-testid="provider-logo">
        <img src={url} alt={provider} loading="lazy" onError={() => setFailed(true)}
          style={{ width: size * 0.66, height: size * 0.66, objectFit: "contain" }} />
      </span>
    );
  }
  return (
    <span className={`${box} text-primary`} style={style} data-testid="provider-logo-fallback">
      {FallbackIcon ? <Icon as={FallbackIcon} size={size} /> : <span className="font-mono text-[10px] font-bold text-muted-foreground">{initials(provider)}</span>}
    </span>
  );
}

function Icon({ as: As, size }) {
  return <As style={{ width: size * 0.58, height: size * 0.58 }} weight="bold" />;
}

/**
 * Brand icon for a client organization. Renders the org's logo (by website domain)
 * with a graceful fallback to a colored initials avatar.
 */
export function OrgLogo({ name, website, logoUrl, size = 36, rounded = false, className = "" }) {
  const domain = domainFromWebsite(website);
  // Try the best source first and fall through on error: uploaded logo →
  // official logo by website domain → official logo by company name → initials.
  const candidates = useMemo(
    () => [logoUrl, domain ? logoUrlForDomain(domain) : null, logoUrlForName(name)].filter(Boolean),
    [logoUrl, domain, name],
  );
  const [idx, setIdx] = useState(0);
  const key = candidates.join("|");
  useEffect(() => setIdx(0), [key]);
  const url = candidates[idx];

  const shape = rounded ? "rounded-full" : "";
  const style = { width: size, height: size };

  if (url) {
    return (
      <span className={`flex shrink-0 items-center justify-center border border-border bg-white ${shape} ${className}`} style={style} data-testid="org-logo">
        <img src={url} alt={name} loading="lazy" onError={() => setIdx((i) => i + 1)}
          style={{ width: size * 0.7, height: size * 0.7, objectFit: "contain" }} />
      </span>
    );
  }
  return (
    <span className={`flex shrink-0 items-center justify-center ${shape} ${className}`}
      style={{ ...style, backgroundColor: avatarColor(name) }} data-testid="org-logo-initials">
      <span className="font-heading font-bold text-white" style={{ fontSize: size * 0.36 }}>{initials(name)}</span>
    </span>
  );
}
